import { useCarouselDots } from '@/components/react/hooks/useCarouselDots';
import { useCarouselPrevNextButtons } from '@/components/react/hooks/useCarouselPrevNextButtons';
import { ReactCarouselControls } from '@/components/react/ui/carousel/ReactCarouselControls';
import { ReactCarouselDots } from '@/components/react/ui/carousel/ReactCarouselDots';
import { cva, cx, type VariantProps } from '@/cva.config';
import type { EmblaPluginType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { type ComponentProps, type ReactNode } from 'react';

export const carouselVariants = cva({
  base: 'embla__container flex touch-pan-y touch-pinch-zoom',
  variants: {
    position: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
    },
  },
});

interface ReactCarouselProps extends VariantProps<typeof carouselVariants> {
  loop?: boolean;
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  controlOptions?: Omit<
    ComponentProps<typeof ReactCarouselControls>,
    'scrollPrev' | 'scrollNext' | 'nextBtnDisabled' | 'prevBtnDisabled'
  >;
  dotOptions?: Omit<
    ComponentProps<typeof ReactCarouselDots>,
    'selectedIndex' | 'scrollSnaps' | 'onDotButtonClick'
  >;
}

export const ReactCarousel = ({
  loop = true,
  children,
  className,
  containerClassName,
  controlOptions,
  dotOptions,
  position,
}: ReactCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
    },
    [WheelGesturesPlugin() as unknown as EmblaPluginType],
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useCarouselDots(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = useCarouselPrevNextButtons(emblaApi);

  const hasControlOverlay =
    controlOptions && controlOptions.position === 'overlaySlides';

  return (
    <div
      className={cx(
        'embla',
        {
          relative: hasControlOverlay,
          'overflow-hidden': !hasControlOverlay,
        },
        className,
      )}
      ref={emblaRef}
    >
      <div
        className={cx(
          carouselVariants({ position }),
          { 'overflow-hidden': hasControlOverlay },
          containerClassName,
        )}
      >
        {children}
      </div>
      {dotOptions && (
        <ReactCarouselDots
          {...dotOptions}
          className={dotOptions.className}
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          onDotButtonClick={onDotButtonClick}
        />
      )}
      {controlOptions && (
        <ReactCarouselControls
          {...controlOptions}
          nextBtnDisabled={nextBtnDisabled}
          prevBtnDisabled={prevBtnDisabled}
          scrollPrev={onPrevButtonClick}
          scrollNext={onNextButtonClick}
        />
      )}
    </div>
  );
};
